import type { GameStatus, GmAction } from '@badger-sentinel/protocol';

import { DATA, DATA_LABEL, LABEL } from '../../theme/type.js';

/**
 * The umpire's control strip.
 *
 * ## The five controls are gated on the status they act against
 *
 * They used to be five always-live buttons of which two or three were always
 * wrong: `START` on a running game, `RESUME` on a game that was never paused.
 * The server refused them and the refusal arrived as an error in the alert
 * lane, which is a slow way to learn that a button was never going to work —
 * and this is the one person in the room who cannot afford to hesitate.
 *
 * The gate is the transition table, not a guess: `START` belongs to `LOBBY`,
 * `RESUME` to `PAUSED`, and `PAUSE` / `+1 MIN` / `CLOSE WINDOW` to `RUNNING`.
 * A control outside its state is disabled rather than hidden, so the strip does
 * not change shape under the pointer and the umpire can see the whole of what
 * the console can do.
 *
 * ## `CLOSE WINDOW` asks first
 *
 * It ends the orders window for all sixteen players and it sat ten pixels from
 * `+1 MIN`, in the same 20px ghost register, with no guard — while cancelling a
 * single queued order had one. It now raises the shell's `gm-force-close`
 * confirm, priced with the clock and how many cells have submitted.
 *
 * ## The status is the loudest thing here
 *
 * It was 11px `--text-muted` at the far right — quieter than the buttons whose
 * legality it decides. It leads the strip now, at readout weight.
 */

export const GM_EXTEND_MS = 60_000;

interface Control {
  readonly action: GmAction;
  readonly label: string;
  readonly testId: string;
  readonly extendMs?: number;
  /** The statuses this control is legal in. */
  readonly legalIn: readonly GameStatus[];
  /** Raises a confirm rather than acting. */
  readonly guarded?: boolean;
}

const CONTROLS: readonly Control[] = Object.freeze([
  { action: 'START', label: 'START', testId: 'gm-start', legalIn: ['LOBBY'] },
  { action: 'PAUSE', label: 'PAUSE', testId: 'gm-pause', legalIn: ['RUNNING'] },
  { action: 'RESUME', label: 'RESUME', testId: 'gm-resume', legalIn: ['PAUSED'] },
  {
    action: 'EXTEND',
    label: '+1 MIN',
    testId: 'gm-extend',
    extendMs: GM_EXTEND_MS,
    legalIn: ['RUNNING'],
  },
  {
    action: 'FORCE_CLOSE',
    label: 'CLOSE WINDOW',
    testId: 'gm-close',
    legalIn: ['RUNNING'],
    guarded: true,
  },
]);

export interface GmControlStripProps {
  readonly status: GameStatus | null;
  readonly busy: boolean;
  readonly onControl: (action: GmAction, extendMs?: number) => void;
  /** Asks the shell for the confirm a guarded control needs. */
  readonly onRequestForceClose: () => void;
  readonly forceCloseArmed: boolean;
}

export function GmControlStrip(props: GmControlStripProps): JSX.Element {
  const status = props.status;

  return (
    <div
      data-mat="chrome"
      data-testid="gm-panel"
      style={{
        flex: 'none',
        height: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span style={LABEL}>CONTROL</span>
      <span data-testid="gm-status" style={{ ...DATA_LABEL, fontSize: 12, color: 'var(--text)' }}>
        {status ?? '—'}
      </span>
      <span style={{ flex: 'none', width: 1, height: 14, background: 'var(--rule)' }} />

      {CONTROLS.map((control) => {
        const legal = status !== null && control.legalIn.includes(status);
        const armed = control.guarded === true && props.forceCloseArmed;
        return (
          <button
            key={control.action}
            type="button"
            data-testid={control.testId}
            disabled={props.busy || !legal}
            aria-expanded={control.guarded === true ? armed : undefined}
            onClick={() =>
              control.guarded === true
                ? props.onRequestForceClose()
                : props.onControl(control.action, control.extendMs)
            }
            style={{
              flex: 'none',
              height: 20,
              padding: '0 10px',
              background: armed ? 'var(--red-surface)' : 'transparent',
              border: `1px solid ${armed ? 'var(--red)' : 'var(--border-strong)'}`,
              color: armed ? 'var(--red-text)' : legal ? 'var(--text-2)' : 'var(--text-disabled)',
              ...DATA_LABEL,
              fontSize: 11,
              fontWeight: 600,
              cursor: legal && !props.busy ? 'pointer' : 'default',
            }}
          >
            {control.label}
          </button>
        );
      })}

      <span style={{ flex: 1 }} />
      {props.busy && <span style={{ ...DATA, color: 'var(--text-muted)' }}>WORKING</span>}
    </div>
  );
}
